import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const MarkupCalculator = () => {
  const [costPrice, setCostPrice] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [markupPercentage, setMarkupPercentage] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);
  const [solutionSteps, setSolutionSteps] = useState([]);

  const handleCostPriceChange = (e) => {
    setCostPrice(e.target.value);
    setError('');
  };

  const handleSellingPriceChange = (e) => {
    setSellingPrice(e.target.value);
    setError('');
  };

  const handleMarkupPercentageChange = (e) => {
    setMarkupPercentage(e.target.value);
    setError('');
  };

  const calculateMarkup = () => {
    if (!costPrice || (!sellingPrice && !markupPercentage)) {
      setError('Please enter cost price and either selling price or markup percentage.');
      return;
    }

    const cp = parseFloat(costPrice);
    const sp = sellingPrice ? parseFloat(sellingPrice) : null;
    const mp = markupPercentage ? parseFloat(markupPercentage) : null;

    if (isNaN(cp) || cp <= 0) {
      setError('Please enter a valid positive cost price.');
      return;
    }

    if (sp && (isNaN(sp) || sp <= cp)) {
      setError('Please enter a valid selling price greater than cost price.');
      return;
    }

    if (mp && (isNaN(mp) || mp < 0)) {
      setError('Please enter a valid markup percentage (0 or greater).');
      return;
    }

    let calculatedValue;
    let calculationType;

    if (sp) {
      // Calculate markup percentage from selling price
      calculatedValue = ((sp - cp) / cp) * 100;
      calculationType = 'markupPercentage';
    } else {
      // Calculate selling price from markup percentage
      calculatedValue = cp * (1 + mp / 100);
      calculationType = 'sellingPrice';
    }

    setResult(calculatedValue);

    // Update chart data
    setChartData({
      labels: ['Cost Price', 'Selling Price', 'Markup'],
      datasets: [
        {
          label: 'Markup Calculation',
          data: [cp, sp || calculatedValue, calculatedValue],
          backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(54, 162, 235, 0.5)', 'rgba(255, 206, 86, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(54, 162, 235, 1)', 'rgba(255, 206, 86, 1)'],
          borderWidth: 1
        }
      ]
    });

    // Update solution steps
    setSolutionSteps([
      `Cost Price: ${cp.toFixed(2)}`,
      ...(calculationType === 'markupPercentage' ? [
        `Selling Price: ${sp.toFixed(2)}`,
        `Markup Calculation: ((Selling Price - Cost Price) / Cost Price) * 100`,
        `Markup: ((${sp.toFixed(2)} - ${cp.toFixed(2)}) / ${cp.toFixed(2)}) * 100 = ${calculatedValue.toFixed(2)}%`
      ] : [
        `Markup Percentage: ${mp.toFixed(2)}%`,
        `Selling Price Calculation: Cost Price * (1 + Markup Percentage / 100)`,
        `Selling Price: ${cp.toFixed(2)} * (1 + ${mp.toFixed(2)}/100) = ${calculatedValue.toFixed(2)}`
      ])
    ]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    calculateMarkup();
  };

  return (
    <>
      <Helmet>
        <title>Markup Calculator | BioLab Tools</title>
        <meta name="description" content="Calculate markup percentage or selling price with ease. Determine pricing strategies for your products using this calculator." />
        <meta name="keywords" content="markup calculator, selling price calculator, business calculator, pricing calculator" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <header className="mb-8 text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">📊</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Markup Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Calculate markup percentage or selling price with ease. Determine pricing strategies for your products.
            </p>
          </header>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cost Price</label>
                <input
                  type="number"
                  value={costPrice}
                  onChange={handleCostPriceChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter cost price"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Selling Price (Optional)</label>
                <input
                  type="number"
                  value={sellingPrice}
                  onChange={handleSellingPriceChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter selling price (leave empty to calculate)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Markup Percentage (Optional)</label>
                <input
                  type="number"
                  value={markupPercentage}
                  onChange={handleMarkupPercentageChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter markup percentage (leave empty to calculate)"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-green-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-green-700 transition duration-200"
              >
                Calculate Markup
              </button>

              {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-md mt-4">
                  {error}
                </div>
              )}

              {result !== null && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Result</h2>
                  <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    {sellingPrice ? (
                      <p className="text-lg font-medium">Markup Percentage: {result.toFixed(2)}%</p>
                    ) : (
                      <p className="text-lg font-medium">Selling Price: {result.toFixed(2)}</p>
                    )}
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Visualization</h3>
                    <div className="h-96">
                      <Bar
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: { display: true, text: 'Value' }
                            },
                            x: { title: { display: true, text: 'Category' } }
                          },
                          plugins: { title: { display: true, text: 'Markup Calculation' } }
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Solution Steps</h3>
                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                      <ul className="list-disc pl-6 space-y-2">
                        {solutionSteps.map((step, index) => (
                          <li key={index} className="text-gray-600">{step}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions (FAQ)</h2>
            <div className="space-y-6">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What is markup?</h3>
                <p className="text-gray-600">
                  Markup is the difference between the selling price and cost price, expressed as a percentage of the cost price. It's used to determine pricing strategies in business.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How is markup calculated?</h3>
                <p className="text-gray-600">
                  Markup can be calculated in two ways:
                  <br />
                  1. From Selling Price: [(Selling Price - Cost Price) / Cost Price] * 100
                  <br />
                  2. To find Selling Price: Cost Price * (1 + Markup Percentage / 100)
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What's the difference between markup and margin?</h3>
                <p className="text-gray-600">
                  Markup is calculated based on cost price, while margin is calculated based on selling price. Markup shows how much profit is added to the cost, while margin shows the profit as a percentage of the selling price.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How accurate are the calculations?</h3>
                <p className="text-gray-600">
                  The calculations are highly accurate, using standard mathematical formulas. Results are rounded to two decimal places for readability.
                </p>
              </div>
            </div>
          </div>

          {/* How to Use Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use the Markup Calculator</h2>
            <div className="space-y-8">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Cost Price</h3>
                <p className="text-gray-600">
                  Input the cost price of your product or service.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Choose Calculation Method</h3>
                <p className="text-gray-600">
                  You can either:
                  <br />
                  1. Enter the selling price to calculate markup percentage
                  <br />
                  2. Enter the markup percentage to calculate selling price
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Calculate</h3>
                <p className="text-gray-600">
                  Click the "Calculate Markup" button to get the result.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 4: View Result and Visualization</h3>
                <p className="text-gray-600">
                  The result will be displayed along with a bar chart and solution steps.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MarkupCalculator;